/**
 * Web route tree.
 *
 * Structure:
 * - Public: /login, /signup, /auth/*
 * - Authenticated with sidebar: /, /admin/*
 *
 * Auth checks use localStorage cache directly (no context needed).
 */

import { createRootRoute, createRoute, redirect } from "@tanstack/react-router";

import {
  AdminDashboardConnected,
  AdminDebugConnected,
  AdminFoodsConnected,
  AdminModifiersConnected,
  AdminReleasesConnected,
  AdminUsersConnected,
} from "@animus/dom-features";
import {
  isAuthenticated,
  isLoggedIn,
} from "@animus/frontend-features/auth-store";
import { hasRole } from "@animus/frontend-features/rbac";

import { webAuthCache } from "../auth/adapter";
import { LoginPage } from "../screens/auth/login.tsx";
import { NativeLoginPage } from "../screens/auth/native-login.tsx";
import { SignupPage } from "../screens/auth/signup.tsx";
import { Home } from "../screens/home.tsx";
import { AppLayout } from "./layouts/app-layout.tsx";
import { RootComponent } from "./root.tsx";

// ============================================
// Root Route
// ============================================

const rootRoute = createRootRoute({
  component: RootComponent,
});

// ============================================
// Auth Routes (redirect if already logged in)
// ============================================

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  beforeLoad: () => {
    // Only redirect if user is actually logged in (not anonymous)
    // Anonymous users should be able to access login/signup
    if (isLoggedIn(webAuthCache)) {
      throw redirect({ to: "/" });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/signup",
  component: SignupPage,
});

// ============================================
// App Layout Routes (authenticated with sidebar)
// ============================================

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  component: AppLayout,
  beforeLoad: ({ location }) => {
    // No cached user = redirect to login
    // Note: The auth provider implements "guaranteed user" pattern and
    // auto-creates anonymous sessions, so this is just for cold start
    // before the cache is populated.
    if (!isAuthenticated(webAuthCache)) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
});

const homeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: Home,
});

// ============================================
// Admin Routes (require admin or moderator role)
// ============================================

const adminLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: "_admin",
  beforeLoad: () => {
    if (!hasRole(webAuthCache, "moderator")) {
      throw redirect({ to: "/" });
    }
  },
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin",
  component: AdminDashboardConnected,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/users",
  component: AdminUsersConnected,
});

const adminFoodsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/foods",
  component: AdminFoodsConnected,
});

const adminModifiersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/modifiers",
  component: AdminModifiersConnected,
});

const adminReleasesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/releases",
  component: AdminReleasesConnected,
});

const adminDebugRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/debug",
  component: AdminDebugConnected,
});

// ============================================
// Native Auth Routes (public - handles its own auth)
// ============================================

const nativeLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/native-login",
  component: NativeLoginPage,
});

const desktopLoginRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/desktop-login",
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/auth/native-login", search });
  },
});

// ============================================
// Route Tree
// ============================================

export const routeTree = rootRoute.addChildren([
  nativeLoginRoute,
  desktopLoginRedirect,
  authLayoutRoute.addChildren([loginRoute, signupRoute]),
  appLayoutRoute.addChildren([
    homeRoute,
    adminLayoutRoute.addChildren([
      adminDashboardRoute,
      adminUsersRoute,
      adminFoodsRoute,
      adminModifiersRoute,
      adminReleasesRoute,
      adminDebugRoute,
    ]),
  ]),
]);
